import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR, SHIPPING_TIME, SHIPPING_METHOD, products } from '../data/mock';
import { FileText } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions | EC Tool Works';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-slate-700 text-xs font-semibold mb-4">
              <FileText className="w-3.5 h-3.5" />
              LEGAL
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
              Terms & Conditions
            </h1>
            <p className="text-gray-900/70 max-w-lg">
              Please read these terms carefully before placing an order or using our services
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-sm">
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-2 text-sm">
                <span className="font-semibold text-gray-900">Effective date:</span> {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
              <p className="text-gray-700 mb-8">
                Welcome to EC Tool Works (the "Site"), operated by <span className="font-semibold text-gray-900">{CONTACT_INFO.corpName}</span> ("we," "us," or "our"). By accessing or using this Site or placing an order through it, you ("customer," "you" or "your") agree to be bound by these Terms and Conditions ("Terms"). If you do not agree, do not use the Site.
              </p>

              {/* 1. Acceptance */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By creating an account, placing an order, or otherwise using the Site, you confirm that you are at least 18 years of age (or the age of majority in your jurisdiction), are legally capable of entering into a binding contract, and that all information you provide is accurate, complete and current.
              </p>

              {/* 2. Products & Pricing */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">2. Products, Availability and Pricing</h2>
              <p className="text-gray-700 mb-4">
                All products listed on the Site are subject to availability. We reserve the right to limit the quantity of items purchased per person, per household, or per order, and to refuse or cancel any order at our sole discretion (including, but not limited to, suspected fraud, pricing errors, or product unavailability). All prices are displayed in U.S. Dollars (USD) and are exclusive of any applicable taxes, shipping or handling charges, which will be added at checkout. Prices and product information are subject to change without notice.
              </p>
              <p className="text-gray-700 mb-4">
                The following table lists all products currently offered for sale on the Site and their corresponding prices:
              </p>
              <div className="overflow-x-auto mb-4 border border-gray-200 rounded-xl" data-testid="terms-products-table-wrapper">
                <table className="w-full text-sm" data-testid="terms-products-table">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-900">Product Name</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Price (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, idx) => (
                      <tr
                        key={product.id}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                        data-testid={`terms-product-row-${product.id}`}
                      >
                        <td className="px-4 py-3 text-gray-700">{product.name}</td>
                        <td className="px-4 py-3 text-right text-gray-900 font-semibold whitespace-nowrap">
                          ${product.price.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-gray-600 text-xs mb-4 italic">
                Prices listed above are subject to change without notice. The price charged for any product will be the price displayed on the product page at the time the order is placed.
              </p>

              {/* 3. Order Process */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">3. Order Process & Confirmation</h2>
              <p className="text-gray-700 mb-4">
                When you place an order, you are making an offer to purchase. Your order is not accepted until we send you an order confirmation email. We reserve the right to refuse any order at our discretion. If we cancel your order after payment has been authorized, we will issue a full refund to the original payment method.
              </p>

              {/* 4. Payment Terms */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">4. Payment Terms</h2>
              <p className="text-gray-700 mb-4">
                We accept major credit and debit cards (Visa, Mastercard, American Express) and other payment methods displayed at checkout. By submitting your payment information, you authorize us (or our third-party payment processor) to charge the full order amount, including products, applicable taxes, shipping and handling, to your selected payment method.
              </p>
              <p className="text-gray-700 mb-4">
                Charges will appear on your credit card or bank statement as "<span className="text-athletic-600 font-semibold">{CARD_DESCRIPTOR}</span>". All payment information is transmitted using industry-standard encryption. We do not store your full credit card information on our servers.
              </p>

              {/* 5. Currency */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">5. Currency</h2>
              <p className="text-gray-700 mb-4">
                All transactions on this Site are processed in U.S. Dollars (USD). We only accept payments from U.S.&#8209;issued credit or debit cards billed to a U.S. address. Orders placed with non&#8209;U.S. payment methods or billing addresses cannot be processed.
              </p>

              {/* 6. Billing & Shipping */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">6. Billing & Shipping Address</h2>
              <p className="text-gray-700 mb-4">
                You must provide a valid billing address that exactly matches the address on file with your card issuer. You may provide a separate shipping address. We are not responsible for non-delivery, loss, or delay caused by an inaccurate, incomplete or undeliverable address provided by you.
              </p>

              {/* 7. Shipping Policy */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">7. Shipping Policy</h2>
              <p className="text-gray-700 mb-4">
                We currently ship only within the United States. Orders are processed within 1–2 business days (excluding weekends and U.S. holidays) and are shipped via {SHIPPING_METHOD}. Estimated delivery time after dispatch is {SHIPPING_TIME}. We offer free USPS standard shipping on every order — no minimum purchase required. Risk of loss and title pass to you upon delivery to the carrier. We are not responsible for carrier delays, weather, or events outside our reasonable control.
              </p>

              {/* 8. Returns & Refunds */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">8. Returns, Exchanges & Refunds</h2>
              <p className="text-gray-700 mb-4">
                We offer a 30-day return policy from the date of delivery on unused items in their original, undamaged packaging with all original tags and accessories. Items that show signs of use, are damaged by the customer, or are missing parts will not be accepted. To initiate a return, please email <a href={`mailto:${CONTACT_INFO.email}`} className="text-athletic-600 hover:underline font-semibold">{CONTACT_INFO.email}</a> with your order number to receive a Return Merchandise Authorization (RMA).
              </p>
              <p className="text-gray-700 mb-4">
                Approved refunds are issued to the original payment method within 5–10 business days after we receive and inspect your return. Original shipping charges are non-refundable, and return shipping costs are the customer's responsibility unless the return is due to our error (wrong item, defective product). Please review our full <a href="/returns" className="text-athletic-600 hover:underline font-semibold">Returns Policy</a> for additional details.
              </p>

              {/* 9. Cancellations */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">9. Order Cancellations</h2>
              <p className="text-gray-700 mb-4">
                Order cancellation requests must be received before the order has shipped. Once shipped, the order will be subject to our Returns Policy. To request a cancellation, contact us at <a href={`mailto:${CONTACT_INFO.email}`} className="text-athletic-600 hover:underline font-semibold">{CONTACT_INFO.email}</a> as soon as possible.
              </p>

              {/* 10. Chargebacks */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">10. Chargebacks & Disputes</h2>
              <p className="text-gray-700 mb-4">
                If you believe a charge on your statement is incorrect or you have a dispute regarding an order, please contact us first at <a href={`mailto:${CONTACT_INFO.email}`} className="text-athletic-600 hover:underline font-semibold">{CONTACT_INFO.email}</a> or <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-athletic-600 hover:underline font-semibold">{CONTACT_INFO.phone}</a> so we can resolve the matter directly. Initiating a chargeback without first contacting us may delay resolution and could result in a temporary or permanent suspension of your account.
              </p>

              {/* 11. Taxes */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">11. Sales Tax</h2>
              <p className="text-gray-700 mb-4">
                Applicable U.S. state and local sales tax will be calculated and added to your order at checkout based on your shipping address.
              </p>

              {/* 12. Restricted Use */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">12. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">
                You may not use the Site for any unlawful, fraudulent, abusive, or harmful purpose, including but not limited to: making purchases with stolen or unauthorized payment methods; reverse engineering, scraping, or interfering with the Site; or violating any applicable law or regulation. We reserve the right to suspend or terminate accounts engaged in such activity.
              </p>

              {/* 13. IP */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">13. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                All content on this Site, including text, graphics, logos, product images, page layouts, and trademarks, is the property of {CONTACT_INFO.corpName} or its licensors and is protected by U.S. and international copyright and trademark laws. You may not copy, reproduce, distribute, modify, or create derivative works from any content on this Site without our prior written permission.
              </p>

              {/* 14. Disclaimer */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">14. Disclaimers</h2>
              <p className="text-gray-700 mb-4">
                The Site and all products are provided on an "as-is" and "as-available" basis. To the fullest extent permitted by law, we disclaim all warranties, express or implied, including without limitation implied warranties of merchantability, fitness for a particular purpose, and non-infringement. Use of hardware tools and power equipment may carry inherent safety considerations; you assume full responsibility for safe use of any equipment purchased.
              </p>

              {/* 15. Limitation */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">15. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, in no event shall {CONTACT_INFO.corpName}, its directors, officers, employees, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Site or any product purchased through the Site. Our total aggregate liability for any claim arising under these Terms shall not exceed the amount you actually paid for the product giving rise to the claim.
              </p>

              {/* 16. Indemnification */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">16. Indemnification</h2>
              <p className="text-gray-700 mb-4">
                You agree to indemnify, defend and hold harmless {CONTACT_INFO.corpName} and its affiliates from and against any claims, damages, losses, liabilities and expenses (including reasonable attorneys' fees) arising from or related to your violation of these Terms or your misuse of the Site or products.
              </p>

              {/* 17. Privacy */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">17. Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our collection and use of your personal information is governed by our <a href="/privacy" className="text-athletic-600 hover:underline font-semibold">Privacy Policy</a>, which is incorporated by reference into these Terms.
              </p>

              {/* 18. Modifications */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">18. Modifications to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. Updated Terms will be posted on this page with a revised "Effective Date." Your continued use of the Site after any change constitutes acceptance of the updated Terms.
              </p>

              {/* 19. Governing Law */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">19. Governing Law & Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by and construed in accordance with the laws of the State of Illinois, United States, without regard to its conflict-of-laws principles. Any dispute arising from or related to these Terms or your use of the Site shall be resolved in the state or federal courts located in Cook County, Illinois, and you consent to the personal jurisdiction of those courts.
              </p>

              {/* 20. Severability */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">20. Severability & Entire Agreement</h2>
              <p className="text-gray-700 mb-4">
                If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect. These Terms, together with our Privacy Policy and Returns Policy, constitute the entire agreement between you and {CONTACT_INFO.corpName} concerning your use of the Site.
              </p>

              {/* 21. Contact */}
              <h2 className="text-xl font-display font-bold text-gray-900 mt-8 mb-4">21. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms, your order, returns, or any other matter, please contact us:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <p className="text-gray-900 font-semibold">{CONTACT_INFO.corpName}</p>
                <p className="text-gray-600">DBA EC Tool Works</p>
                <p className="text-gray-600">{CONTACT_INFO.address.street}</p>
                <p className="text-gray-600">{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
                <p className="text-gray-600 mt-2">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-athletic-600 hover:underline">{CONTACT_INFO.email}</a></p>
                <p className="text-gray-600">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-athletic-600 hover:underline">{CONTACT_INFO.phone}</a></p>
              </div>

              <p className="text-gray-500 text-sm mt-8">
                Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsPage;
